import { useEffect, useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  BarChart, Bar, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import { 
  Building2, Plus, Search, Users, FileText, Database, 
  Settings, Eye, Trash2, CheckCircle, XCircle, BarChart3,
  Shield, Mail, Globe, Calendar, User, TrendingUp, AlertTriangle,
  Activity, PieChartIcon, Edit, UserCog, Copy, Link as LinkIcon, RefreshCcw, Skull
} from 'lucide-react';
import { TenantManageDialog } from '@/components/TenantManageDialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

// Mirror of backend `normalize_slug` (utils/tenant_plans.py)
const normalizeSlug = (raw) => {
  if (!raw) return '';
  return String(raw)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9-]/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const copyToClipboard = (value, label = 'Slug') => {
  if (!value) return;
  navigator.clipboard?.writeText(value)
    .then(() => toast.success(`${label} copiado para o clipboard`))
    .catch(() => toast.error('Não foi possível copiar'));
};

const TenantsPage = () => {
  const { user } = useOutletContext();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const [aggregatedStats, setAggregatedStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [editingPlatforms, setEditingPlatforms] = useState(false);
  const [editedPlatforms, setEditedPlatforms] = useState([]);
  const [managedTenant, setManagedTenant] = useState(null);
  const [isManageDialogOpen, setIsManageDialogOpen] = useState(false);
  
  const [newTenant, setNewTenant] = useState({
    name: '',
    domain: '',
    contact_email: '',
    admin_name: '',
    admin_email: '',
    admin_password: '',
    dpo_email: '',
    address: '',
    integration_platforms: []
  });

  // Available integration platforms
  const INTEGRATION_PLATFORMS = [
    { id: 'autoline', name: 'Autoline', description: 'Sistema de gestão automóvel' },
    { id: 'egoi', name: 'e-Goi', description: 'Plataforma de marketing automation' },
    { id: 'closum', name: 'Closum', description: 'CRM e gestão de leads' }
  ];

  useEffect(() => {
    fetchTenants();
    fetchAggregatedStats();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tenants`, { withCredentials: true });
      setTenants(response.data);
    } catch (error) {
      console.error('Error fetching tenants:', error);
      toast.error('Erro ao carregar tenants');
    } finally {
      setLoading(false);
    }
  };

  const fetchAggregatedStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/analytics/super-admin/aggregated`, { withCredentials: true });
      setAggregatedStats(response.data);
    } catch (error) {
      console.error('Error fetching aggregated stats:', error);
    }
  };

  const handleCreateTenant = async () => {
    if (!newTenant.name || !newTenant.domain || !newTenant.contact_email || 
        !newTenant.admin_name || !newTenant.admin_email || !newTenant.admin_password) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/tenants`, newTenant, { withCredentials: true });
      toast.success(`Tenant "${newTenant.name}" criado com sucesso!`);
      setIsCreateDialogOpen(false);
      setNewTenant({
        name: '', domain: '', contact_email: '', admin_name: '',
        admin_email: '', admin_password: '', dpo_email: '', address: '',
        integration_platforms: []
      });
      fetchTenants();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar tenant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewTenant = async (tenant) => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tenants/${tenant.tenant_id}`, { withCredentials: true });
      setSelectedTenant(response.data);
      setEditedPlatforms(response.data.integration_platforms || []);
      setEditingPlatforms(false);
      setIsDetailDialogOpen(true);
    } catch (error) {
      toast.error('Erro ao carregar detalhes do tenant');
    }
  };

  const handleSavePlatforms = async () => {
    if (!selectedTenant) return;
    
    setSubmitting(true);
    try {
      await axios.patch(
        `${BACKEND_URL}/api/tenants/${selectedTenant.tenant_id}`,
        { integration_platforms: editedPlatforms },
        { withCredentials: true }
      );
      
      // Update selected tenant with new platforms
      setSelectedTenant(prev => ({
        ...prev,
        integration_platforms: editedPlatforms
      }));
      
      setEditingPlatforms(false);
      toast.success('Plataformas de integração atualizadas');
      fetchTenants(); // Refresh list
    } catch (error) {
      toast.error('Erro ao atualizar plataformas');
    } finally {
      setSubmitting(false);
    }
  };

  const togglePlatform = (platformId) => {
    setEditedPlatforms(prev => 
      prev.includes(platformId)
        ? prev.filter(p => p !== platformId)
        : [...prev, platformId]
    );
  };

  const handleDeactivateTenant = async (tenantId) => {
    if (!window.confirm('Tem a certeza que deseja desativar este tenant?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/tenants/${tenantId}`, { withCredentials: true });
      toast.success('Tenant desativado com sucesso');
      fetchTenants();
    } catch (error) {
      toast.error('Erro ao desativar tenant');
    }
  };

  const handleHardDeleteTenant = async (tenant) => {
    const expected = tenant.slug || tenant.tenant_id;
    const answer = window.prompt(
      `⚠️ ELIMINAÇÃO PERMANENTE\n\nEsta ação apaga o tenant "${tenant.name}" e TODOS os seus dados (utilizadores, consentimentos, documentos, etc.) — IRREVERSÍVEL.\n\nEscreva o slug exato para confirmar: ${expected}`
    );
    if (answer !== expected) {
      if (answer !== null) toast.error('Confirmação inválida — eliminação cancelada');
      return;
    }
    try {
      const { data } = await axios.delete(
        `${BACKEND_URL}/api/super-admin/tenants/${tenant.tenant_id}/hard`,
        { data: { confirm: expected }, withCredentials: true }
      );
      toast.success(data.message || 'Tenant eliminado permanentemente');
      fetchTenants();
      fetchAggregatedStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao eliminar tenant');
    }
  };

  const handleResetDemoData = async (tenant) => {
    if (!window.confirm(
      `Repor dados de DEMO do tenant "${tenant.name}"?\n\n` +
      `• Apaga: consentimentos, titulares, campanhas, RTBF, breaches, etc.\n` +
      `• Preserva: tenant, admins, configuração do tenant e config global\n` +
      `• Repopula: 5 titulares demo + consentimentos exemplo\n\nContinuar?`
    )) return;
    try {
      const { data } = await axios.post(
        `${BACKEND_URL}/api/super-admin/tenants/${tenant.tenant_id}/reset-demo`,
        { confirm: 'RESET', seed_defaults: true, reset_users: false, reset_tenant_config: false },
        { withCredentials: true }
      );
      const del = Object.keys(data.per_collection_deleted || {}).length;
      const seed = Object.keys(data.seeded || {}).length;
      toast.success(`✅ Reset concluído — apagadas ${del} coleção(ões), repopuladas ${seed}`);
      fetchTenants();
      fetchAggregatedStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao repor dados demo');
    }
  };

  const filteredTenants = tenants.filter(t => 
    t.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.domain?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalStats = {
    totalTenants: tenants.length,
    activeTenants: tenants.filter(t => t.is_active).length,
    totalUsers: tenants.reduce((sum, t) => sum + (t.statistics?.users_count || 0), 0),
    totalConsents: tenants.reduce((sum, t) => sum + (t.statistics?.consents_count || 0), 0)
  };

  if (user?.role !== 'super_admin' && user?.role !== 'admin') {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-red-500">Acesso negado. Apenas Super Admins podem aceder a esta página.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="tenants-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Gestão de Tenants
          </h1>
          <p className="text-slate-600">
            Administração de organizações e suas bases de dados isoladas
          </p>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button 
              data-testid="create-tenant-button"
              className="bg-[#10B981] hover:bg-[#10B981]/90 text-white"
            >
              <Plus className="w-4 h-4 mr-2" />
              Novo Tenant
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Criar Novo Tenant</DialogTitle>
              <DialogDescription>
                Criar uma nova organização com base de dados isolada e administrador
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Nome da Organização *</Label>
                <Input
                  data-testid="tenant-name-input"
                  value={newTenant.name}
                  onChange={(e) => setNewTenant({...newTenant, name: e.target.value})}
                  placeholder="Empresa XYZ"
                />
              </div>
              <div className="space-y-2">
                <Label>Domínio *</Label>
                <Input
                  data-testid="tenant-domain-input"
                  value={newTenant.domain}
                  onChange={(e) => setNewTenant({...newTenant, domain: e.target.value})}
                  placeholder="empresa-xyz.com"
                />
              </div>
              <div className="col-span-2 space-y-1 -mt-2">
                {(() => {
                  const preview = normalizeSlug(newTenant.domain || newTenant.name);
                  if (!preview) return null;
                  const loginUrl = `${window.location.origin}/login-local?org=${preview}`;
                  return (
                    <div
                      className="flex flex-wrap items-center gap-2 rounded-md bg-slate-50 border border-slate-200 px-3 py-2 text-xs"
                      data-testid="slug-preview"
                    >
                      <span className="text-slate-500">Slug gerado:</span>
                      <code className="font-mono text-slate-800 bg-white border rounded px-1.5 py-0.5">
                        {preview}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(preview, 'Slug')}
                        className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                        data-testid="copy-slug-preview"
                      >
                        <Copy className="w-3 h-3" /> copiar
                      </button>
                      <span className="text-slate-300">•</span>
                      <LinkIcon className="w-3 h-3 text-slate-400" />
                      <span className="text-slate-500 truncate">{loginUrl}</span>
                      <button
                        type="button"
                        onClick={() => copyToClipboard(loginUrl, 'URL de login')}
                        className="text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                        data-testid="copy-login-url-preview"
                      >
                        <Copy className="w-3 h-3" /> copiar URL
                      </button>
                    </div>
                  );
                })()}
              </div>
              <div className="space-y-2">
                <Label>Email de Contacto *</Label>
                <Input
                  type="email"
                  value={newTenant.contact_email}
                  onChange={(e) => setNewTenant({...newTenant, contact_email: e.target.value})}
                  placeholder="contacto@empresa.com"
                />
              </div>
              <div className="space-y-2">
                <Label>Email do DPO</Label>
                <Input
                  type="email"
                  value={newTenant.dpo_email}
                  onChange={(e) => setNewTenant({...newTenant, dpo_email: e.target.value})}
                  placeholder="dpo@empresa.com"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Morada</Label>
                <Input
                  value={newTenant.address}
                  onChange={(e) => setNewTenant({...newTenant, address: e.target.value})}
                  placeholder="Rua Example, 123, Lisboa"
                />
              </div>
              
              <div className="col-span-2 border-t pt-4 mt-2">
                <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Administrador do Tenant
                </h4>
              </div>
              
              <div className="space-y-2">
                <Label>Nome do Admin *</Label>
                <Input
                  data-testid="admin-name-input"
                  value={newTenant.admin_name}
                  onChange={(e) => setNewTenant({...newTenant, admin_name: e.target.value})}
                  placeholder="João Silva"
                />
              </div>
              <div className="space-y-2">
                <Label>Email do Admin *</Label>
                <Input
                  type="email"
                  data-testid="admin-email-input"
                  value={newTenant.admin_email}
                  onChange={(e) => setNewTenant({...newTenant, admin_email: e.target.value})}
                  placeholder="admin@empresa.com"
                />
              </div>
              <div className="col-span-2 space-y-2">
                <Label>Password do Admin *</Label>
                <Input
                  type="password"
                  data-testid="admin-password-input"
                  value={newTenant.admin_password}
                  onChange={(e) => setNewTenant({...newTenant, admin_password: e.target.value})}
                  placeholder="Mínimo 8 caracteres"
                />
              </div>
            </div>

            {/* Integration Platforms */}
            <div className="space-y-3 border-t pt-4 mt-4">
              <Label className="text-base font-semibold">Plataformas de Integração</Label>
              <p className="text-sm text-slate-500">Selecione as plataformas externas que este tenant irá utilizar</p>
              <div className="grid grid-cols-3 gap-4">
                {INTEGRATION_PLATFORMS.map((platform) => (
                  <div 
                    key={platform.id}
                    className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                      newTenant.integration_platforms.includes(platform.id) 
                        ? 'border-[#10B981] bg-emerald-50' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                    onClick={() => {
                      const platforms = newTenant.integration_platforms.includes(platform.id)
                        ? newTenant.integration_platforms.filter(p => p !== platform.id)
                        : [...newTenant.integration_platforms, platform.id];
                      setNewTenant({...newTenant, integration_platforms: platforms});
                    }}
                  >
                    <Checkbox
                      id={`platform-${platform.id}`}
                      checked={newTenant.integration_platforms.includes(platform.id)}
                      className="mt-1"
                    />
                    <div className="grid gap-0.5 leading-none">
                      <label htmlFor={`platform-${platform.id}`} className="text-sm font-medium cursor-pointer">
                        {platform.name}
                      </label>
                      <p className="text-xs text-slate-500">{platform.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleCreateTenant} 
                disabled={submitting}
                className="bg-[#10B981] hover:bg-[#10B981]/90"
              >
                {submitting ? 'A criar...' : 'Criar Tenant'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs for Overview/Dashboard and Tenants List */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="overview" className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4" />
            Dashboard Agregado
          </TabsTrigger>
          <TabsTrigger value="tenants" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Lista de Tenants
          </TabsTrigger>
        </TabsList>

        {/* Dashboard Agregado Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          {aggregatedStats ? (
            <>
              {/* Global KPIs */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="border-l-4 border-l-blue-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Tenants</p>
                        <p className="text-3xl font-bold text-slate-900">{aggregatedStats.summary?.tenants_count || 0}</p>
                        <p className="text-xs text-slate-400 mt-1">Organizações ativas</p>
                      </div>
                      <div className="p-3 bg-blue-100 rounded-full">
                        <Building2 className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-purple-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Utilizadores</p>
                        <p className="text-3xl font-bold text-purple-600">{aggregatedStats.summary?.total_users || 0}</p>
                        <p className="text-xs text-slate-400 mt-1">{aggregatedStats.summary?.total_titulares || 0} titulares</p>
                      </div>
                      <div className="p-3 bg-purple-100 rounded-full">
                        <Users className="w-6 h-6 text-purple-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-green-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Total Consentimentos</p>
                        <p className="text-3xl font-bold text-green-600">{aggregatedStats.summary?.total_consents || 0}</p>
                        <p className="text-xs text-green-600 mt-1">{aggregatedStats.summary?.active_consents || 0} ativos</p>
                      </div>
                      <div className="p-3 bg-green-100 rounded-full">
                        <FileText className="w-6 h-6 text-green-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-l-amber-500">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-slate-500">Compliance Global</p>
                        <p className="text-3xl font-bold text-amber-600">{aggregatedStats.summary?.overall_compliance || 0}%</p>
                        <Progress value={aggregatedStats.summary?.overall_compliance || 0} className="mt-2 h-2" />
                      </div>
                      <div className="p-3 bg-amber-100 rounded-full">
                        <TrendingUp className="w-6 h-6 text-amber-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Compliance by Tenant */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="w-5 h-5" />
                      Compliance por Tenant
                    </CardTitle>
                    <CardDescription>Taxa de consentimentos ativos por organização</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {aggregatedStats.tenants?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart data={aggregatedStats.tenants.slice(0, 8)} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} stroke="#94A3B8" />
                          <YAxis 
                            dataKey="name" 
                            type="category" 
                            tick={{ fontSize: 10 }} 
                            stroke="#94A3B8" 
                            width={120}
                            tickFormatter={(val) => val.length > 15 ? val.substring(0, 15) + '...' : val}
                          />
                          <Tooltip 
                            formatter={(value) => [`${value}%`, 'Compliance']}
                            contentStyle={{ backgroundColor: '#fff', border: '1px solid #E2E8F0', borderRadius: '8px' }}
                          />
                          <Bar dataKey="compliance_score" radius={[0, 4, 4, 0]}>
                            {aggregatedStats.tenants.slice(0, 8).map((entry, index) => (
                              <Cell 
                                key={`cell-${index}`} 
                                fill={entry.compliance_score >= 70 ? '#10B981' : entry.compliance_score >= 40 ? '#F59E0B' : '#EF4444'} 
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[300px] text-slate-400">
                        Sem dados de tenants
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Users Distribution */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <PieChartIcon className="w-5 h-5" />
                      Distribuição de Utilizadores
                    </CardTitle>
                    <CardDescription>Utilizadores por tenant</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {aggregatedStats.tenants?.length > 0 ? (
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={aggregatedStats.tenants.filter(t => t.users > 0).slice(0, 6)}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={100}
                            paddingAngle={2}
                            dataKey="users"
                            nameKey="name"
                            label={({ name, percent }) => `${name.substring(0, 10)}... ${(percent * 100).toFixed(0)}%`}
                          >
                            {aggregatedStats.tenants.filter(t => t.users > 0).slice(0, 6).map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[300px] text-slate-400">
                        Sem dados de utilizadores
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Alerts & Issues */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500" />
                    Alertas e Atenção
                  </CardTitle>
                  <CardDescription>Tenants que precisam de atenção</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                        <div>
                          <p className="text-lg font-bold text-red-600">{aggregatedStats.summary?.total_breaches || 0}</p>
                          <p className="text-sm text-red-700">Violações de Dados</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                      <div className="flex items-center gap-3">
                        <Users className="w-6 h-6 text-amber-600" />
                        <div>
                          <p className="text-lg font-bold text-amber-600">{aggregatedStats.summary?.total_rtbf || 0}</p>
                          <p className="text-sm text-amber-700">Pedidos RTBF</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-3">
                        <FileText className="w-6 h-6 text-blue-600" />
                        <div>
                          <p className="text-lg font-bold text-blue-600">{aggregatedStats.summary?.revoked_consents || 0}</p>
                          <p className="text-sm text-blue-700">Consen. Revogados</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Tenant Rankings Table */}
              <Card>
                <CardHeader>
                  <CardTitle>Ranking de Compliance por Tenant</CardTitle>
                  <CardDescription>Ordenado por taxa de compliance</CardDescription>
                </CardHeader>
                <CardContent>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>#</TableHead>
                        <TableHead>Organização</TableHead>
                        <TableHead>Utilizadores</TableHead>
                        <TableHead>Consentimentos</TableHead>
                        <TableHead>Compliance</TableHead>
                        <TableHead>Estado</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {aggregatedStats.tenants?.map((tenant, index) => (
                        <TableRow key={tenant.tenant_id}>
                          <TableCell>
                            <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                              index === 0 ? 'bg-yellow-100 text-yellow-700' : 
                              index === 1 ? 'bg-slate-100 text-slate-700' : 
                              index === 2 ? 'bg-amber-100 text-amber-700' : 
                              'bg-slate-50 text-slate-600'
                            }`}>
                              {index + 1}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <span className="font-medium">{tenant.name}</span>
                            </div>
                          </TableCell>
                          <TableCell>{tenant.users}</TableCell>
                          <TableCell>
                            <div>
                              <span className="font-semibold">{tenant.consents}</span>
                              <span className="text-xs text-green-600 ml-1">({tenant.active_consents} ativos)</span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Progress value={tenant.compliance_score} className="w-20 h-2" />
                              <span className={`text-sm font-medium ${
                                tenant.compliance_score >= 70 ? 'text-green-600' : 
                                tenant.compliance_score >= 40 ? 'text-amber-600' : 'text-red-600'
                              }`}>
                                {tenant.compliance_score}%
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={tenant.compliance_score >= 70 ? "success" : tenant.compliance_score >= 40 ? "warning" : "destructive"}>
                              {tenant.compliance_score >= 70 ? 'Bom' : tenant.compliance_score >= 40 ? 'Atenção' : 'Crítico'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          )}
        </TabsContent>

        {/* Lista de Tenants Tab */}
        <TabsContent value="tenants" className="space-y-6 mt-6">
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          data-testid="search-tenants-input"
          placeholder="Pesquisar por nome ou domínio..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Tenants Table */}
      <Card>
        <CardHeader>
          <CardTitle>Organizações Registadas</CardTitle>
          <CardDescription>Lista de todas as organizações com bases de dados isoladas</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">A carregar...</div>
          ) : filteredTenants.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-2 text-slate-300" />
              <p>Nenhum tenant encontrado</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organização</TableHead>
                  <TableHead>Slug</TableHead>
                  <TableHead>Domínio</TableHead>
                  <TableHead>Base de Dados</TableHead>
                  <TableHead>Utilizadores</TableHead>
                  <TableHead>Consentimentos</TableHead>
                  <TableHead>DPO</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTenants.map((tenant) => (
                  <TableRow key={tenant.tenant_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{tenant.name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {tenant.slug ? (
                        <div className="flex items-center gap-1" data-testid={`tenant-slug-${tenant.tenant_id}`}>
                          <code className="font-mono text-xs bg-slate-100 border border-slate-200 rounded px-1.5 py-0.5 text-slate-700">
                            {tenant.slug}
                          </code>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-slate-400 hover:text-slate-700"
                            onClick={() => copyToClipboard(tenant.slug, 'Slug')}
                            data-testid={`copy-slug-${tenant.tenant_id}`}
                            title="Copiar slug"
                          >
                            <Copy className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-amber-600" title="Slug em falta — execute o backfill">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Globe className="w-3 h-3 text-slate-400" />
                        {tenant.domain}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="font-mono text-xs">
                        <Database className="w-3 h-3 mr-1" />
                        {tenant.database_name || 'N/A'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold">{tenant.statistics?.users_count || 0}</span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{tenant.statistics?.consents_count || 0}</span>
                        <span className="text-xs text-green-600">
                          ({tenant.statistics?.active_consents || 0} ativos)
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={tenant.dpo_mode === 'cogniseg' ? 'default' : 'outline'}
                        data-testid={`dpo-mode-${tenant.tenant_id}`}
                      >
                        {tenant.dpo_mode === 'cogniseg' ? 'DPO as a Service' : 'DPO do cliente'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {tenant.is_active ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          Ativo
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="w-3 h-3 mr-1" />
                          Inativo
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewTenant(tenant)}
                          data-testid={`view-tenant-${tenant.tenant_id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/tenant-dashboard/${tenant.tenant_id}`)}
                          data-testid={`dashboard-tenant-${tenant.tenant_id}`}
                        >
                          <BarChart3 className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => { setManagedTenant(tenant); setIsManageDialogOpen(true); }}
                          data-testid={`manage-tenant-${tenant.tenant_id}`}
                        >
                          <UserCog className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleResetDemoData(tenant)}
                          className="text-blue-600 hover:text-blue-700"
                          data-testid={`reset-demo-${tenant.tenant_id}`}
                          title="Repor dados de demo (mantém tenant e config)"
                        >
                          <RefreshCcw className="w-4 h-4" />
                        </Button>
                        {tenant.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeactivateTenant(tenant.tenant_id)}
                            className="text-amber-600 hover:text-amber-700"
                            data-testid={`deactivate-tenant-${tenant.tenant_id}`}
                            title="Desativar (soft delete — reversível)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleHardDeleteTenant(tenant)}
                          className="text-red-700 hover:text-red-800"
                          data-testid={`hard-delete-tenant-${tenant.tenant_id}`}
                          title="Eliminar permanentemente (hard delete — IRREVERSÍVEL)"
                        >
                          <Skull className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>
      </Tabs>

      {/* Tenant Detail Dialog */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {selectedTenant?.name}
            </DialogTitle>
            <DialogDescription>
              Detalhes da organização e estatísticas
            </DialogDescription>
          </DialogHeader>
          
          {selectedTenant && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Domínio</p>
                  <p className="font-medium flex items-center gap-1">
                    <Globe className="w-4 h-4" />
                    {selectedTenant.domain}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Slug</p>
                  {selectedTenant.slug ? (
                    <div className="flex items-center gap-2" data-testid="detail-tenant-slug">
                      <code className="font-mono text-sm bg-slate-100 border border-slate-200 rounded px-2 py-1 text-slate-800">
                        {selectedTenant.slug}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-slate-700"
                        onClick={() => copyToClipboard(selectedTenant.slug, 'Slug')}
                        data-testid="detail-copy-slug"
                        title="Copiar slug"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-amber-600">
                      Slug em falta — corra o backfill em Configurações da Plataforma
                    </p>
                  )}
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Base de Dados</p>
                  <p className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">
                    {selectedTenant.database_name}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Email de Contacto</p>
                  <p className="font-medium flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {selectedTenant.contact_email}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Criado em</p>
                  <p className="font-medium flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    {new Date(selectedTenant.created_at).toLocaleDateString('pt-PT')}
                  </p>
                </div>
                {selectedTenant.slug && (
                  <div className="col-span-2 space-y-1">
                    <p className="text-sm text-slate-500">URL de login local</p>
                    <div className="flex items-center gap-2">
                      <LinkIcon className="w-4 h-4 text-slate-400" />
                      <code className="font-mono text-xs bg-slate-50 border border-slate-200 rounded px-2 py-1 text-slate-700 truncate flex-1">
                        {window.location.origin}/login-local?org={selectedTenant.slug}
                      </code>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-slate-400 hover:text-slate-700"
                        onClick={() => copyToClipboard(`${window.location.origin}/login-local?org=${selectedTenant.slug}`, 'URL de login')}
                        data-testid="detail-copy-login-url"
                        title="Copiar URL"
                      >
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <h4 className="font-semibold mb-3">Estatísticas</h4>
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-blue-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{selectedTenant.statistics?.users_count || 0}</p>
                    <p className="text-xs text-slate-600">Utilizadores</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{selectedTenant.statistics?.active_consents || 0}</p>
                    <p className="text-xs text-slate-600">Consen. Ativos</p>
                  </div>
                  <div className="bg-amber-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-600">{selectedTenant.statistics?.pending_consents || 0}</p>
                    <p className="text-xs text-slate-600">Pendentes</p>
                  </div>
                  <div className="bg-red-50 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">{selectedTenant.statistics?.revoked_consents || 0}</p>
                    <p className="text-xs text-slate-600">Revogados</p>
                  </div>
                </div>
              </div>

              {/* Integration Platforms */}
              <div className="border-t pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold">Plataformas de Integração</h4>
                  {!editingPlatforms ? (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => {
                        setEditedPlatforms(selectedTenant.integration_platforms || []);
                        setEditingPlatforms(true);
                      }}
                      data-testid="edit-platforms-btn"
                    >
                      <Edit className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => {
                          setEditedPlatforms(selectedTenant.integration_platforms || []);
                          setEditingPlatforms(false);
                        }}
                      >
                        Cancelar
                      </Button>
                      <Button 
                        size="sm" 
                        onClick={handleSavePlatforms}
                        disabled={submitting}
                        className="bg-[#10B981] hover:bg-[#10B981]/90"
                        data-testid="save-platforms-btn"
                      >
                        {submitting ? 'A guardar...' : 'Guardar'}
                      </Button>
                    </div>
                  )}
                </div>
                
                {editingPlatforms ? (
                  <div className="grid grid-cols-3 gap-3">
                    {INTEGRATION_PLATFORMS.map((platform) => {
                      const isActive = editedPlatforms.includes(platform.id);
                      return (
                        <div 
                          key={platform.id}
                          className={`flex items-start space-x-3 p-3 border rounded-lg cursor-pointer transition-colors ${
                            isActive 
                              ? 'border-[#10B981] bg-emerald-50' 
                              : 'border-slate-200 hover:border-slate-300'
                          }`}
                          onClick={() => togglePlatform(platform.id)}
                        >
                          <Checkbox
                            id={`edit-platform-${platform.id}`}
                            checked={isActive}
                            className="mt-1"
                          />
                          <div className="grid gap-0.5 leading-none">
                            <label htmlFor={`edit-platform-${platform.id}`} className="text-sm font-medium cursor-pointer">
                              {platform.name}
                            </label>
                            <p className="text-xs text-slate-500">{platform.description}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-3">
                    {INTEGRATION_PLATFORMS.map((platform) => {
                      const isActive = selectedTenant.integration_platforms?.includes(platform.id);
                      return (
                        <div 
                          key={platform.id}
                          className={`flex items-center gap-2 p-3 rounded-lg border ${
                            isActive ? 'border-[#10B981] bg-emerald-50' : 'border-slate-200 bg-slate-50 opacity-50'
                          }`}
                        >
                          <div className={`w-2 h-2 rounded-full ${isActive ? 'bg-[#10B981]' : 'bg-slate-300'}`} />
                          <span className={`text-sm font-medium ${isActive ? 'text-emerald-700' : 'text-slate-500'}`}>
                            {platform.name}
                          </span>
                          {isActive && (
                            <Badge variant="outline" className="ml-auto text-xs bg-white">Ativo</Badge>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {selectedTenant.admins?.length > 0 && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3">Administradores</h4>
                  <div className="space-y-2">
                    {selectedTenant.admins.map((admin, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <User className="w-4 h-4 text-slate-400" />
                        <div>
                          <p className="font-medium text-sm">{admin.name}</p>
                          <p className="text-xs text-slate-500">{admin.email}</p>
                        </div>
                        <Badge variant="outline" className="ml-auto">{admin.role}</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDetailDialogOpen(false)}>
              Fechar
            </Button>
            <Button onClick={() => {
              setIsDetailDialogOpen(false);
              navigate(`/tenant-dashboard/${selectedTenant?.tenant_id}`);
            }}>
              Ver Dashboard
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TenantManageDialog
        tenant={managedTenant}
        open={isManageDialogOpen}
        onOpenChange={setIsManageDialogOpen}
        onSaved={fetchTenants}
      />
    </div>
  );
};

export default TenantsPage;
